import { lowerCase, sortBy, upperFirst } from 'lodash/fp';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom-v5-compat';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import Pagination from 'dashboard/components/Pagination';
import Pii from 'dashboard/components/Pii';
import Tooltip from 'dashboard/components/Tooltip';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import TableLayout, { TableLinkTR, TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { DocumentGeneric } from 'dashboard/models/document';
import NavTo from 'dashboard/views/DashboardAccountsShowView/components/NavTo';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

interface AccountDocumentsSectionProps {
  featureFlags: FeatureFlagsMap;
  documents: DocumentGeneric[];
  isLoading: boolean;
  isLoadingDocuments: boolean;
  paginationParams: PaginationParams;
  pageSize: number;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
}

const AccountDocumentsSection = ({
  documents,
  isLoading,
  isLoadingDocuments,
  featureFlags,
  paginationParams,
  pageSize,
  handlePageLinkClick,
  handlePageSizeClick,
}: AccountDocumentsSectionProps) => {
  const paginationEnabled = useFeatureFlag('dashboard:accounts-paginate-documents');
  const loading = paginationEnabled ? isLoadingDocuments : isLoading;
  return (
    <Card>
      <Card.Body>
        <Card.Title>Documents</Card.Title>
        <TableLayout
          dataTest='account-documents__table'
          isLoading={loading}
          showHeader={documents.length !== 0}
          isEmpty={documents.length === 0}
          nullState={
            <TableNullState hasUsedFilters={false} dataTestBlock='account-documents'>
              <h3>Looks like there aren&#39;t any documents for this account yet.</h3>
            </TableNullState>
          }
          headerTR={
            <>
              {!paginationEnabled && <TableTH>{/* Thumbnail */}</TableTH>}
              <TableTH>File name</TableTH>
              <TableTH>Document type</TableTH>
              <TableTH>Date processed</TableTH>
              <TableTH>{/* Actions */}</TableTH>
            </>
          }
        >
          {sortBy([(o) => DateTime.fromISO(o.attributes['created-at'])], documents)
            .reverse()
            .map((document) =>
              paginationEnabled ? (
                <NoThumbnailDocumentDetail
                  key={document.id}
                  type={document.attributes.kind}
                  document={document}
                  featureFlags={featureFlags}
                />
              ) : (
                <DocumentDetail
                  key={document.id}
                  type={document.attributes.kind}
                  document={document}
                  featureFlags={featureFlags}
                />
              ),
            )}
        </TableLayout>
        {paginationEnabled && documents.length > 0 && (
          <Pagination
            pageSize={pageSize}
            paginationParams={paginationParams}
            handlePageSizeClick={handlePageSizeClick}
            handlePageLinkClick={handlePageLinkClick}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default AccountDocumentsSection;

const ThumbnailTD = styled(TableTD)`
  width: 80px;
`;

const FileNameTD = styled(TableTD)`
  width: 25%;
`;

const FileTypeTD = styled(TableTD)`
  width: 25%;
`;

const ProcessedAtTD = styled(TableTD)`
  width: 25%;
`;

const LinksTD = styled(TableTD)`
  display: table-cell;
  justify-content: space-between;
`;

const NoDocumentText = styled('div')`
  color: ${COLORS.gray50};
`;

const IconGroupContainer = styled('div')`
  float: right;
  width: 40px;
`;

interface DocumentDetailProps {
  type: string;
  document: DocumentGeneric;
  featureFlags: FeatureFlagsMap;
}

const NoThumbnailDocumentDetail = ({ type, document }: DocumentDetailProps) => {
  const [showActions, { enable: enableShowActions, disable: disableShowActions }] =
    useToggle(false);

  const formattedDate =
    document.attributes['processed-at'] != null
      ? DateTime.fromISO(document.attributes['processed-at']).toFormat(DATE_AND_TIME_TZ_FORMAT)
      : null;
  const documentViewerLink = `/dashboard/document-viewer/${document.id}`;

  return (
    <TableLinkTR
      data-test={`account-documents__table-row--${document.id || 'no-document'}`}
      id={`account-documents__table-row--${document.id || 'no-document'}`}
      onMouseEnter={enableShowActions}
      onMouseLeave={disableShowActions}
      to={documentViewerLink}
      state={{ internalRedirect: true }}
    >
      <FileNameTD>
        <p>{document.attributes.filenames?.[0]}</p>
      </FileNameTD>
      <FileTypeTD>
        <p>{upperFirst(lowerCase(type))}</p>
      </FileTypeTD>
      <ProcessedAtTD>
        {formattedDate ? <p>{formattedDate}</p> : <NoDocumentText>No date</NoDocumentText>}
      </ProcessedAtTD>
      <LinksTD>
        <IconGroupContainer>
          {showActions && document.relationships.inquiry?.data?.id && (
            <Tooltip placement='top' trigger='hover' tooltip='View Inquiry' hideArrow={false}>
              <NavTo to={`/dashboard/inquiries/${document.relationships.inquiry.data.id}`} />
            </Tooltip>
          )}
        </IconGroupContainer>
      </LinksTD>
    </TableLinkTR>
  );
};

const DocumentDetail = ({ type, document }: DocumentDetailProps) => {
  const { files, 'files-normalized': filesNormalized } = document.attributes || {};
  // if the document has not been processed it does not have normalized files
  // we default to using normalized files since some image formats that are not compatible
  // with browsers (HEIC images) have been converted to images
  const displayFiles = files.length === filesNormalized.length ? filesNormalized : files;

  const [showActions, { enable: enableShowActions, disable: disableShowActions }] =
    useToggle(false);

  const formattedDate =
    document.attributes['processed-at'] != null
      ? DateTime.fromISO(document.attributes['processed-at']).toFormat(DATE_AND_TIME_TZ_FORMAT)
      : null;
  const documentViewerLink = `/dashboard/document-viewer/${document.id}`;

  return (
    <TableTR
      data-test={`account-documents__table-row--${document.id || 'no-document'}`}
      id={`account-documents__table-row--${document.id || 'no-document'}`}
      onMouseEnter={enableShowActions}
      onMouseLeave={disableShowActions}
    >
      <ThumbnailTD>
        <Pii isRedacted={false /* AsyncImage has its own empty state */}>
          <Link to={documentViewerLink} state={{ internalRedirect: true }}>
            <AsyncImage
              value={displayFiles.map((file) => ({
                url: file.url,
                label: file.filename,
                name: file.filename,
                type: file['file-type'],
              }))}
              showCount
              showExtension
              width='80px'
              height='80px'
              allowPreviewZoom={false}
            />
          </Link>
        </Pii>
      </ThumbnailTD>
      <FileNameTD>
        <p>{displayFiles?.[0]?.filename}</p>
      </FileNameTD>
      <FileTypeTD>
        <p>{upperFirst(lowerCase(type))}</p>
      </FileTypeTD>
      <ProcessedAtTD>
        {formattedDate ? <p>{formattedDate}</p> : <NoDocumentText>No date</NoDocumentText>}
      </ProcessedAtTD>
      <LinksTD>
        <IconGroupContainer>
          {showActions && document.relationships.inquiry?.data?.id && (
            <Tooltip placement='top' trigger='hover' tooltip='View Inquiry' hideArrow={false}>
              <NavTo to={`/dashboard/inquiries/${document.relationships.inquiry.data.id}`} />
            </Tooltip>
          )}
        </IconGroupContainer>
      </LinksTD>
    </TableTR>
  );
};
